import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';
import {
  KeycloakUserCreateRequest,
  KeycloakUserUpdateRequest,
  ManagementService,
  User,
} from 'src/app/api/generated';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from 'src/app/confirmation-dialog/confirmation-dialog.component';
import { DataChangedService } from 'src/app/data-changed.service';

import * as uuid from 'uuid';
import { isUserAdmin } from '../tools';
import { UserConfigComponent } from '../user-config/user-config.component';

@Component({
    selector: 'app-user-config-dialog',
    templateUrl: './user-config-dialog.component.html',
    styleUrls: ['./user-config-dialog.component.scss'],
    standalone: false
})
export class UserConfigDialogComponent implements OnInit {
  public editMode = false;
  public userForm: UntypedFormGroup;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dialogRef: MatDialogRef<User>,
    @Inject(MAT_DIALOG_DATA) public data: User,
    private managementService: ManagementService,
    private dataChangedService: DataChangedService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.editMode = this.data?.id !== undefined;
    this.userForm = this.formBuilder.group({
      id: [this.data?.id ? this.data?.id : uuid.v4()],
      email: [this.data?.email ? this.data?.email : ''],
      firstName: [this.data?.first_name ? this.data?.first_name : ''],
      lastName: [this.data?.last_name ? this.data?.last_name : ''],
      admin: [this.data?.roles ? isUserAdmin(this.data) : false],
      roles: [this.data?.roles ? this.data?.roles : ''],
    });
  }

  deleteUser() {
    // Confirm deletion
    const deleteDialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: <ConfirmationDialogData>{
        headlineText: `Benutzer ${this.data.email} endgültig entfernen? `,
        confirmationButtonText: 'Ja',
        rejectButtonText: 'Nein',
        displayText: `ACHTUNG: Benutzer wird auch von zentralen Authentifizierungsservice gelöscht.`,
      },
    });
    deleteDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.managementService
          .managementUsersDetailDelete(this.userForm.value.id)
          .subscribe((res) => {
            this.dataChangedService.dataUpdated('user');
            this.snackBar.open(`Benutzer ${this.data.email} gelöscht.`, null, {
              duration: 1500,
            });
            this.dialogRef.close();
          });
      }
    });
  }

  resetPassword() {
    if (!this.editMode) {
      return;
    }

    this.managementService
      .managementUsersDetailPasswordResetUpdate(this.userForm.value.id)
      .subscribe((user) => {
        if (user.password_tmp) {
          this.dialog.open(ConfirmationDialogComponent, {
            data: <ConfirmationDialogData>{
              headlineText: `Temporäres Passwort für ${user.email}`,
              confirmationButtonText: 'OK',
              rejectButtonText: '',
              displayText: `Temporäres Passwort: ${user.password_tmp}`,
            },
            hasBackdrop: false,
          });
        }
      });
  }

  async updateOrCreateUser() {
    if (this.userForm.invalid) {
      return;
    }

    const userDetails = {
      email: this.userForm.value.email,
      first_name: this.userForm.value.firstName,
      last_name: this.userForm.value.lastName,
      roles: this.userForm.value.admin,
    };

    let updateOrCreateObservable: Observable<User>;
    if (this.editMode) {
      const updateRequest: KeycloakUserUpdateRequest = userDetails;
      updateOrCreateObservable = this.managementService.managementUsersDetailUpdate(
        this.userForm.value.id,
        updateRequest
      );
    } else {
      const createRequest: KeycloakUserCreateRequest = {
        username: this.userForm.value.email,
        ...userDetails,
      };
      updateOrCreateObservable = this.managementService.managementUsersDetailCreate(
        this.userForm.value.id,
        createRequest
      );
    }
    updateOrCreateObservable.subscribe((user) => {
      if (user.password_tmp) {
        this.dialog.open(ConfirmationDialogComponent, {
          data: <ConfirmationDialogData>{
            headlineText: `Temporäres Passwort für ${user.email}`,
            confirmationButtonText: 'OK',
            rejectButtonText: '',
            displayText: `Temporäres Passwort: ${user.password_tmp}`,
          },
          hasBackdrop: false,
        });
      }
      this.dataChangedService.dataUpdated('user');
      this.snackBar.open(
        `Benutzer ${this.userForm.value.email} aktualisiert.`,
        null,
        {
          duration: 1500,
        }
      );
      this.dialogRef.close();
    });
  }
}
